
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, User, Calendar, Plus, Minus, Edit, Activity } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { cn } from '@/lib/utils';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useSupabaseUser } from '@/hooks/use-supabase-user';

interface ScheduleChange {
  id: string;
  change_type: 'added' | 'removed' | 'modified';
  guard_id: string;
  team_id: string;
  schedule_id: string;
  previous_data: any;
  new_data: any;
  created_at: string;
  created_by: string;
  profiles: {
    first_name: string;
    last_name: string;
    full_name: string;
  } | null;
  teams: {
    name: string;
  } | null;
}

export const ScheduleLogsList = () => {
  const { isAdmin } = useUserRoles();
  const { user } = useSupabaseUser();

  const { data: changes, isLoading } = useQuery({
    queryKey: ['schedule-changes-all', user?.id, isAdmin],
    queryFn: async () => {
      if (!user?.id) return [];

      // For admins, get guards from their admin group
      let guardIdsFilter: string[] | null = null;
      
      if (isAdmin) {
        // First, get all members of the admin group (parent + contributors)
        const { data: adminGroupMembers } = await supabase
          .from('admin_team')
          .select('member_id')
          .eq('admin_id', user.id)
          .in('member_type', ['parent_admin', 'contributor']);
        
        if (adminGroupMembers && adminGroupMembers.length > 0) {
          const adminIds = adminGroupMembers.map(m => m.member_id);
          
          // Now get all guards created by these admins
          const { data: guards } = await supabase
            .from('profiles')
            .select('id')
            .in('created_by', adminIds);
          
          if (guards && guards.length > 0) {
            guardIdsFilter = guards.map(g => g.id);
          } else {
            // No guards found, return empty
            return [];
          }
        } else {
          // No admin group found, return empty
          return [];
        }
      }

      let query = supabase
        .from('shift_changes')
        .select(`
          *,
          profiles:guard_id (first_name, last_name, full_name),
          teams:team_id (name)
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      // Apply admin group filter for admins
      if (isAdmin && guardIdsFilter) {
        query = query.in('guard_id', guardIdsFilter);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data || []) as any;
    },
    enabled: !!user?.id
  });

  const getChangeTypeColor = (type: string) => {
    switch (type) {
      case 'added': return 'bg-green-500/10 text-green-600 dark:bg-green-500/20 dark:text-green-400 border border-green-500/20';
      case 'removed': return 'bg-red-500/10 text-red-600 dark:bg-red-500/20 dark:text-red-400 border border-red-500/20';
      case 'modified': return 'bg-blue-500/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400 border border-blue-500/20';
      default: return 'bg-muted/50 text-muted-foreground border border-border';
    }
  };

  const getChangeTypeLabel = (type: string) => {
    switch (type) {
      case 'added': return 'Added';
      case 'removed': return 'Removed';
      case 'modified': return 'Modified';
      default: return type;
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      if (isValid(date)) {
        return format(date, 'MMM dd, yyyy HH:mm');
      }
    } catch {
      // Fallback for invalid dates
    }
    return dateString;
  };

  const formatShiftData = (data: any) => {
    if (!data) return 'N/A';
    
    const startDate = data.start_date ? formatDateTime(data.start_date) : 'N/A';
    const endDate = data.end_date ? formatDateTime(data.end_date) : 'N/A';
    const location = data.location || 'N/A';
    
    return `${startDate} - ${endDate} at ${location}`;
  };

  const getShiftTitle = (data: any) => {
    return data?.title || 'N/A';
  };

  const getShiftDescription = (data: any) => {
    return data?.description || 'N/A';
  };

  const getShiftLocation = (data: any) => {
    return data?.location || 'N/A';
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-lg font-medium text-muted-foreground">Loading schedule changes...</p>
        </div>
      </div>
    );
  }

  if (!changes?.length) {
    return (
      <Card className="bg-gradient-to-br from-muted/30 to-muted/50 border-primary/20">
        <CardContent className="text-center py-16">
          <div className="flex flex-col items-center space-y-6">
            <div className="p-6 bg-gradient-to-br from-primary/10 to-primary/20 rounded-2xl">
              <Calendar className="h-16 w-16 text-primary" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-muted-foreground">No Schedule Changes</h3>
              <p className="text-muted-foreground/70">No schedule modifications have been recorded.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-2">
      {changes.map((change, index) => (
        <Card 
          key={change.id} 
          className={cn(
            "group transform transition-all duration-500 hover:scale-[1.02] hover:shadow-luxury",
            "bg-gradient-to-br from-background/95 via-background/90 to-muted/50 border-primary/20",
            "hover:border-primary/40 backdrop-blur-sm"
          )}
          style={{ animationDelay: `${index * 100}ms` }}
        >
          <div className="relative overflow-hidden">
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 group-hover:from-primary/10 group-hover:to-accent/10 transition-all duration-300"></div>
            
            {/* Shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
            
            <div className="relative p-6 space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "p-2 rounded-xl transition-all duration-300 group-hover:scale-110",
                    change.change_type === 'added' ? 'bg-green-500 text-white' :
                    change.change_type === 'removed' ? 'bg-red-500 text-white' :
                    'bg-blue-500 text-white'
                  )}>
                    {change.change_type === 'added' && <Plus className="h-4 w-4" />}
                    {change.change_type === 'removed' && <Minus className="h-4 w-4" />}
                    {change.change_type === 'modified' && <Edit className="h-4 w-4" />}
                  </div>
                  
                  <Badge className={cn(
                    "font-medium",
                    getChangeTypeColor(change.change_type)
                  )}>
                    {getChangeTypeLabel(change.change_type)}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 px-3 py-1 rounded-full">
                  <Clock className="w-3 h-3" />
                  {formatDateTime(change.created_at)}
                </div>
              </div>

              {/* User and Team Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
                  <User className="w-4 h-4 text-primary" />
                  <div>
                    <p className="text-xs text-muted-foreground">Guard</p>
                    <p className="text-sm font-semibold">
                      {change.profiles?.full_name || 
                       `${change.profiles?.first_name || ''} ${change.profiles?.last_name || ''}`.trim() || 
                       'Unknown Guard'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
                  <Calendar className="w-4 h-4 text-primary" />
                  <div>
                    <p className="text-xs text-muted-foreground">Team</p>
                    <p className="text-sm font-semibold">{change.teams?.name || 'Unknown Team'}</p>
                  </div>
                </div>
              </div>

              {/* Shift Information */}
              <div className="space-y-3">
                <h4 className="font-semibold text-foreground flex items-center gap-2">
                  <Activity className="h-4 w-4 text-primary" />
                  Shift Details
                </h4>
                
                <div className="grid grid-cols-1 gap-3 p-4 bg-muted/20 rounded-xl">
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground font-medium">Title</p>
                      <p className="font-semibold">
                        {getShiftTitle(change.new_data || change.previous_data)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-medium">Location</p>
                      <p className="font-semibold">
                        {getShiftLocation(change.new_data || change.previous_data)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-medium">Description</p>
                      <p className="font-semibold truncate">
                        {getShiftDescription(change.new_data || change.previous_data)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground font-medium">Start Time</p>
                      <p className="font-semibold">
                        {formatDateTime((change.new_data || change.previous_data)?.start_date)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-medium">End Time</p>
                      <p className="font-semibold">
                        {formatDateTime((change.new_data || change.previous_data)?.end_date)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Change Details */}
              {change.change_type === 'modified' && change.previous_data && change.new_data && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-foreground">Change Summary</h4>
                  <div className="space-y-2">
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg dark:bg-red-500/20">
                      <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-1">From:</p>
                      <p className="text-sm text-red-700 dark:text-red-300">{formatShiftData(change.previous_data)}</p>
                    </div>
                    <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg dark:bg-green-500/20">
                      <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-1">To:</p>
                      <p className="text-sm text-green-700 dark:text-green-300">{formatShiftData(change.new_data)}</p>
                    </div>
                  </div>
                </div>
              )}

              {change.change_type === 'added' && change.new_data && (
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl dark:bg-green-500/20">
                  <p className="text-sm font-medium text-green-600 dark:text-green-400 mb-2">Added Shift:</p>
                  <p className="text-sm text-green-700 dark:text-green-300">{formatShiftData(change.new_data)}</p>
                </div>
              )}

              {change.change_type === 'removed' && change.previous_data && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl dark:bg-red-500/20">
                  <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-2">Removed Shift:</p>
                  <p className="text-sm text-red-700 dark:text-red-300">{formatShiftData(change.previous_data)}</p>
                </div>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};
